import { createSlice } from "@reduxjs/toolkit";

export const stateMachine = {
  IntroCamera: {
    camera: {
      position: [0, 1.04, 3.6911],
      rotation: [0, 0, 0],
      quaternion: [0, 0, 0, 1],
    },
    initialModule: "none",
    modulesAvailable: ["none"],
  },
  Welcome: {
    camera: {
      position: [0, 1.04, 3.6911],
      rotation: [0, 0, 0],
      quaternion: [0, 0, 0, 1],
    },
    initialModule: "none",
    modulesAvailable: ["none"],
  },
  ServiceCenter: {
    camera: {
      position: [0, 1.5, 2],
      rotation: [0, 0, 0],
      quaternion: [0, 0, 0, 1],
    },
    initialModule: "none",
    modulesAvailable: [
      "none",
      "eventRequestForm",
      "patientData",
      "newService",
      "patientSelection",
      "toriCalculator",
      "screenHover",
      "logoInteraction",
      "InstantFocus",
      "optimizedSelection",
      "comparatif",
      "videoTuto1"
    ],
  },
  TechnologyHub: {
    camera: {
      position: [0, 1.6, 1],
      rotation: [0, Math.PI / 1.6, 0],
      quaternion: [0, 0.9009710909884868, 0, 0.43387912280151913],

    },
    initialModule: "none",
    modulesAvailable: ["none", "holoA", "holoB", "holoC", "holoD", "InstantFocusPdf", "Certificate", "Map", "comparatif"],
  },
  OptometryLab: {
    camera: {
      position: [-1, 1.5, 1],
      rotation: [0, Math.PI / 0.42, 0],
      quaternion: [-0, -0.5633157167712972, -0, -0.8262417341422785],

    },
    initialModule: "none",
    modulesAvailable: ["none", "tableHover", 'keratometre', 'biometre', 'boite', , 'OCT', 'echelle', 'consultation'],
  },
  LdCenter: {
    camera: {
      position: [0, 1.5, 0],
      rotation: [0, -Math.PI / 2, 0],
      quaternion: [0, -0.707105958539844, 0, 0.7071076038322939],

    },
    mobileCamera: {  // Ajout de la configuration mobile spécifique
      position: [-0.5, 1.3, 2.5],  // Position plus reculée et légèrement plus basse
      rotation: [0, -Math.PI / 3, 0],  // 60 degrés
      quaternion: [0, -0.5, 0, 0.8660254037844387],
    },
    initialModule: "none",
    modulesAvailable: [
      "none",
      "videoTuto1",
      "videoTuto2",
      "videoTuto3",
      "article1",
      "article2",
    ],
  },
};

/**
 * The `progressionSlice` manages state transitions and actions related to a user's progression in an application.
 *
 * State Fields:
 * - `nextState`: String storing the next state to transition to.
 * - `currentState`: String storing the current state.
 * - `currentModule`: String storing the current module within a state.
 * - `activeCamera`: String storing the identifier of the active camera.
 * - `inTrans`: Boolean indicating if a transition is in progress.
 * - `xpStarted`: Boolean indicating if an experience has started.
 * - `xpLoaded`: Boolean indicating if an experience has loaded.
 * - `showPlane`: Boolean indicating if the plane should be shown.
 * - `isSubmitted`: Boolean indicating if the user has submitted the current state.
 *
 * Reducers:
 * - `setActiveCamera`: Updates the `activeCamera` state.
 * - `setXpStarted`: Sets the `xpStarted` state.
 * - `setXpLoaded`: Sets the `xpLoaded` state.
 * - `setInTrans`: Sets the `inTrans` (transition in progress) state.
 * - `setCurrentModule`: Updates the `currentModule`, validating against `modulesAvailable` from `stateMachine`.
 * - `setCurrentState`: Updates `currentState` and resets `currentModule` to the initial module for that state.
 * - `setNextState`: Sets the `nextState` state if no transition is in progress.
 * - `sendAction`: Validates and updates `nextState` based on actions defined in `stateMachine`.
 * - `setShowPlane`: Sets the `showPlane` state.
 * - `setSubmitted`: Sets the `isSubmitted` state.
 */
export const progressionSlice = createSlice({
  name: "progression",
  initialState: {
    nextState: "IntroCamera",
    currentState: "IntroCamera",
    currentModule: "none",
    activeCamera: "IntroCamera",
    token: null,
    userEmail: null,
    inTrans: false,
    xpStarted: false,
    xpLoaded: false,
    showPlane: false,
    isSubmitted: false,
    activeModals: [],
    savedState: null, // Pour sauvegarder l'état avant de naviguer vers new-product
    skipIntroAnimation: false, // Flag pour sauter l'animation intro après retour de new-product
  },
  reducers: {
    login: (state, action) => {
      state.token = action.payload.token;
      if (action.payload.email) {
        state.userEmail = action.payload.email;
      }
    },
    setActiveCamera: (state, action) => {
      state.activeCamera = action.payload;
    },
    setXpStarted: (state, action) => {
      state.xpStarted = action.payload;
    },
    setXpLoaded: (state, action) => {
      state.xpLoaded = action.payload;
    },
    setInTrans: (state, action) => {
      state.inTrans = action.payload;
    },
    setCurrentModule: (state, action) => {
      console.log("Reducer received:", action.payload);
      if (action.payload === "none") {
        state.currentModule = "none";
        state.activeModals = [];
      } else if (typeof action.payload === 'object') {
        if (action.payload.type === "mainVideoModalRight") {
          state.activeModals.push(action.payload);
        } else {
          state.currentModule = action.payload;
        }
      } else {
        const modules = stateMachine[state.currentState].modulesAvailable;
        if (modules.includes(action.payload)) {
          state.currentModule = action.payload;
        }
      }
    },
    setCurrentState: (state, action) => {
      state.currentState = action.payload;
      state.currentModule = stateMachine[action.payload].initialModule;
    },
    setNextState: (state, action) => {
      if (!state.inTrans) {
        state.nextState = action.payload;
      }
    },
    sendAction: (state, action) => {
      try {
        const listeners = stateMachine[state.currentState].on;

        const checkIsFree = state.currentModule === "none";
        const checkActionExist = listeners.hasOwnProperty(action.payload);

        if (checkIsFree && checkActionExist) {
          state.nextState = listeners[action.payload];
        }
      } catch (e) {
        console.log("Error : " + e);
      }
    },
    setShowPlane: (state, action) => {
      state.showPlane = action.payload;
    },
    setSubmitted: (state, action) => {
      state.isSubmitted = action.payload;
    },
    setVideoModule: (state, action) => {
      state.currentModule = action.payload;
    },
    closeModal: (state, action) => {
      state.activeModals = state.activeModals.filter(
        modal => modal.src !== action.payload
      );
    },
    saveStateBeforeNavigation: (state) => {
      console.log("💾 saveStateBeforeNavigation appelé - état actuel:", {
        currentState: state.currentState,
        activeCamera: state.activeCamera,
        currentModule: state.currentModule,
        token: !!state.token
      });
      
      // Sauvegarder l'état actuel avant de naviguer vers new-product
      // Ne sauvegarder que si l'état est valide et l'utilisateur connecté
      if (state.token && state.currentState && stateMachine[state.currentState]) {
        state.savedState = {
          currentState: state.currentState,
          activeCamera: state.activeCamera || "MainCamera",
          currentModule: state.currentModule || "none",
        };
        console.log("✅ État sauvegardé avant navigation:", state.savedState);
      } else {
        console.warn("⚠️ Impossible de sauvegarder - conditions non remplies:", {
          hasToken: !!state.token,
          currentState: state.currentState,
          hasStateMachine: !!stateMachine[state.currentState]
        });
      }
    },
    restoreSavedState: (state) => {
      console.log("🔄 restoreSavedState appelé - état actuel:", {
        currentState: state.currentState,
        savedState: state.savedState,
        token: !!state.token
      });
      
      // Restaurer l'état sauvegardé (uniquement si connecté)
      if (state.savedState && state.token) {
        // Vérifier que l'état sauvegardé est valide (existe dans stateMachine)
        const savedStateName = state.savedState.currentState;
        console.log("🎯 Tentative de restauration vers:", savedStateName);
        
        if (savedStateName && stateMachine[savedStateName]) {
          // Restaurer l'état sauvegardé
          // Important : mettre nextState = savedStateName pour déclencher la transition
          // et que les boutons soient correctement actifs
          state.nextState = savedStateName;
          state.currentState = "Welcome"; // Partir de Welcome pour la transition
          state.activeCamera = "MainCamera";
          state.currentModule = state.savedState.currentModule || "none";
          state.inTrans = false;
          
          // Activer le flag pour sauter l'animation intro
          state.skipIntroAnimation = true;
          
          console.log("✅ État restauré avec transition vers:", {
            nextState: savedStateName,
            currentState: "Welcome",
            activeCamera: state.activeCamera,
            currentModule: state.currentModule,
            skipIntroAnimation: true
          });
        } else {
          // État sauvegardé invalide, restaurer à Welcome
          console.warn("⚠️ État sauvegardé invalide:", savedStateName);
          state.currentState = "Welcome";
          state.activeCamera = "MainCamera";
          state.currentModule = "none";
          state.nextState = "none";
          state.inTrans = false;
        }
        state.savedState = null;
      } else if (!state.token) {
        // Si pas de token, réinitialiser à IntroCamera
        console.log("❌ Pas de token, retour à IntroCamera");
        state.activeCamera = "IntroCamera";
        state.currentState = "IntroCamera";
        state.nextState = "IntroCamera";
        state.inTrans = false;
        state.currentModule = "none";
      }
    },
    logout: (state) => {
      // Déconnecter l'utilisateur et nettoyer l'état
      state.token = null;
      state.userEmail = null;
      state.activeCamera = "IntroCamera";
      state.currentState = "IntroCamera";
      state.nextState = "IntroCamera";
      state.currentModule = "none";
      state.savedState = null;
      state.isSubmitted = false;
      state.xpStarted = false;
      state.xpLoaded = false;
      state.showPlane = false;
      state.activeModals = [];
      state.skipIntroAnimation = false;
      // Nettoyer aussi le localStorage
      localStorage.removeItem('saviol-state');
    },
    clearSkipIntroFlag: (state) => {
      console.log("🏁 clearSkipIntroFlag - réinitialisation du flag");
      state.skipIntroAnimation = false;
    },
  },
});

export const {
  setCurrentModule,
  sendAction,
  setCurrentState,
  setNextState,
  setInTrans,
  setXpStarted,
  setXpLoaded,
  setActiveCamera,
  login,
  logout,
  setShowPlane,
  setSubmitted,
  setVideoModule,
  closeModal,
  saveStateBeforeNavigation,
  restoreSavedState,
  clearSkipIntroFlag,
} = progressionSlice.actions;

export default progressionSlice.reducer;
